import { getAddress } from "viem";
import type { LauncherClient } from "./launcherClient";
import {
  LauncherError,
  type AdminOutcome,
  type DeployPhase,
  type FactoryStatus,
  type FeeQuote,
  type GasEstimate,
  type Hex,
  type LaunchOutcome,
  type HistoryPage,
  type TokenConfig,
  type VerificationCheck,
  type VerificationResult,
} from "./types";
import { TOKEN_DECIMALS } from "../../config/limits";
import { ACTIVE_CHAIN } from "../../config/chains";

/** Controlled demonstration scenarios (spec Part 8/10). All output is demo data. */
export type MockScenario =
  | "publicPays"
  | "teamExempt"
  | "wrongNetwork"
  | "notConfigured"
  | "paused"
  | "bytecodeMismatch"
  | "userRejected"
  | "pending"
  | "reverted"
  | "replaced"
  | "insufficientBalance"
  | "feeChanged"
  | "successReceipt"
  | "postVerificationFailed"
  | "ownerAdmin"
  | "ownerAdminExempt"
  | "adminEventMismatch"
  | "adminFinalMismatch";

export interface MockOptions {
  scenario?: MockScenario;
  chainId?: number;
  publicFee?: bigint;
  owner?: Hex;
  exemptAccounts?: Hex[];
  balance?: bigint;
}

const DEMO_OWNER = getAddress("0x1111111111111111111111111111111111111111");
// E2E-only: matches the injected wallet stub's default account so owner controls
// are reachable in the dedicated E2E build. Never used in a production build.
const E2E_OWNER = getAddress("0x00000000000000000000000000000000000acc01");
const ADMIN_SCENARIOS = ["ownerAdmin", "ownerAdminExempt", "adminEventMismatch", "adminFinalMismatch"];
// E2E-only: the wallet target used by the remove-exemption browser flow.
const E2E_EXEMPT_TARGET = getAddress("0x00000000000000000000000000000000000acc02");
const DEMO_TOKEN = getAddress("0x000000000000000000000000000000000000dead");
const DEMO_TX = ("0x" + "de".repeat(32)) as Hex;
const ZERO = "0x0000000000000000000000000000000000000000";

export class MockLauncherClient implements LauncherClient {
  readonly isMock = true;
  private readonly o: Required<MockOptions>;

  constructor(options: MockOptions = {}) {
    this.o = {
      scenario: options.scenario ?? "publicPays",
      chainId: options.chainId ?? 46630,
      publicFee: options.publicFee ?? 10_000_000_000_000_000n,
      owner: options.owner ?? (ADMIN_SCENARIOS.includes(options.scenario ?? "") ? E2E_OWNER : DEMO_OWNER),
      exemptAccounts: (options.exemptAccounts ?? []).map((a) => getAddress(a)),
      balance: options.balance ?? 1_000_000_000_000_000_000n,
    };
    // Mutable exemption store so Add/Remove are stateful within a session.
    this.exemptSet = new Set(this.o.exemptAccounts);
    // E2E-only: a target that begins exempt, so a Remove flow is valid.
    if (this.o.scenario === "ownerAdminExempt") this.exemptSet.add(E2E_EXEMPT_TARGET);
  }

  private readonly exemptSet: Set<string>;

  private exempt(account: Hex): boolean {
    if (this.o.scenario === "teamExempt") return true;
    return this.exemptSet.has(getAddress(account));
  }

  async getFactoryStatus(): Promise<FactoryStatus> {
    const configured = this.o.scenario !== "notConfigured";
    const mismatch = this.o.scenario === "bytecodeMismatch";
    return {
      configured,
      address: configured ? getAddress("0x0000000000000000000000000000000000000abc") : null,
      chainId: this.o.chainId,
      bytecodeVerified: !mismatch,
      observedBytecodeHash: configured
        ? mismatch
          ? (("0x" + "00".repeat(32)) as Hex)
          : ACTIVE_CHAIN.expectedFactoryBytecodeHash
        : null,
      paused: this.o.scenario === "paused",
      launchFee: this.o.publicFee,
      owner: this.o.owner,
      demo: true,
    };
  }

  async getLaunchFee(): Promise<bigint> {
    return this.o.publicFee;
  }
  async isFeeExempt(account: Hex): Promise<boolean> {
    return this.exempt(account);
  }
  async effectiveLaunchFee(account: Hex): Promise<bigint> {
    return this.exempt(account) ? 0n : this.o.publicFee;
  }
  async getFeeQuote(account: Hex): Promise<FeeQuote> {
    const exempt = this.exempt(account);
    return { exempt, effectiveFee: exempt ? 0n : this.o.publicFee, publicFee: this.o.publicFee };
  }
  async getOwner(): Promise<Hex> {
    return this.o.owner;
  }
  async isPaused(): Promise<boolean> {
    return this.o.scenario === "paused";
  }
  async getBalance(): Promise<bigint> {
    return this.o.balance;
  }
  async estimateCreateGas(): Promise<GasEstimate> {
    // Realistic, distinct demonstration values: ~850k gas at 0.1 gwei.
    const gasUnits = 850_000n;
    const gasPriceWei = 100_000_000n; // 0.1 gwei
    return {
      gasUnits,
      gasPriceWei,
      estimatedGasCostWei: gasUnits * gasPriceWei, // 0.000085 ETH
      demonstration: true,
    };
  }

  async getMyLaunches(account: Hex): Promise<HistoryPage> {
    return {
      items: [
        {
          tokenAddress: DEMO_TOKEN,
          creator: getAddress(account),
          name: "Demo Token",
          symbol: "DEMO",
          supplySmallestUnits: 1_000_000n * 10n ** TOKEN_DECIMALS,
          txHash: DEMO_TX,
          blockNumber: 1000n,
          logIndex: 0,
        },
      ],
      partial: false,
      failedRanges: [],
      nextCursor: null,
      confirmations: 5,
    };
  }
  async getRecentLaunches(): Promise<HistoryPage> {
    return this.getMyLaunches(DEMO_OWNER);
  }

  async createToken(
    config: TokenConfig,
    account: Hex,
    opts?: { knownFeeAtReview?: bigint; onPhase?: (p: DeployPhase) => void }
  ): Promise<LaunchOutcome> {
    const onPhase = opts?.onPhase;
    onPhase?.("preparing");
    switch (this.o.scenario) {
      case "notConfigured":
        throw new LauncherError("factoryNotConfigured");
      case "wrongNetwork":
        throw new LauncherError("wrongNetwork");
      case "paused":
        throw new LauncherError("factoryPaused");
      case "bytecodeMismatch":
        throw new LauncherError("bytecodeMismatch");
      case "insufficientBalance":
        onPhase?.("checkingBalance");
        throw new LauncherError("insufficientBalance");
      case "feeChanged":
        throw new LauncherError("feeChanged", "Fee changed since review", { was: "0", now: this.o.publicFee.toString() });
      case "userRejected":
        onPhase?.("awaitingApproval");
        throw new LauncherError("userRejected");
      case "reverted":
        onPhase?.("submitted");
        throw new LauncherError("reverted");
      case "replaced":
        onPhase?.("submitted");
        throw new LauncherError("replaced");
      case "pending":
        onPhase?.("submitted");
        onPhase?.("waitingReceipt");
        return new Promise<LaunchOutcome>(() => {});
      default:
        break;
    }

    onPhase?.("revalidatingFactory");
    onPhase?.("estimatingGas");
    onPhase?.("checkingBalance");
    onPhase?.("awaitingApproval");
    onPhase?.("submitted");
    onPhase?.("waitingReceipt");
    onPhase?.("verifying");

    const supplySmallestUnits = config.supplyWhole * 10n ** TOKEN_DECIMALS;
    const feePaid = this.exempt(account) ? 0n : this.o.publicFee;
    const failed = this.o.scenario === "postVerificationFailed";
    const verification = buildMockVerification(config, supplySmallestUnits, failed);

    onPhase?.("confirmed");
    return {
      transactionConfirmed: true,
      verificationPassed: verification.passed,
      verificationFailures: verification.failures,
      verification,
      receipt: {
        tokenAddress: DEMO_TOKEN,
        creator: getAddress(account),
        factoryAddress: getAddress("0x0000000000000000000000000000000000000abc"),
        name: config.name,
        symbol: config.symbol,
        decimals: 18,
        supplyWhole: config.supplyWhole,
        supplySmallestUnits,
        feePaid,
        txHash: DEMO_TX,
        blockNumber: 1234n,
        chainId: this.o.chainId,
        timestamp: Math.floor(Date.now() / 1000),
        expectedFactoryHash: ACTIVE_CHAIN.expectedFactoryBytecodeHash,
        observedFactoryHash: ACTIVE_CHAIN.expectedFactoryBytecodeHash,
        transactionStatus: "success",
        demonstrationMode: true,
      },
    };
  }

  async setFeeExempt(account: Hex, exempt: boolean): Promise<AdminOutcome> {
    const target = getAddress(account) as Hex;
    if (target === ZERO) throw new LauncherError("zeroAddress");
    // Mirror the real contract: an unchanged request reverts (FeeExemptionUnchanged).
    if (this.exempt(target) === exempt) throw new LauncherError("noChange");

    // E2E-only failure variants: confirmed transaction preserved, verified=false.
    const eventExempt = this.o.scenario === "adminEventMismatch" ? !exempt : exempt;
    const finalExempt = this.o.scenario === "adminFinalMismatch" ? !exempt : exempt;
    const failures: VerificationCheck[] = [];
    if (eventExempt !== exempt) failures.push({ key: "adminEvent", label: "FeeExemptionUpdated event matches the request", passed: false });
    if (finalExempt !== exempt) failures.push({ key: "adminFinalState", label: "On-chain exemption matches the request", passed: false });
    const verificationPassed = failures.length === 0;

    // Stateful update ONLY on a verified change (so a later re-read reflects it).
    if (verificationPassed) {
      if (exempt) this.exemptSet.add(target);
      else this.exemptSet.delete(target);
    }

    return {
      txHash: DEMO_TX,
      account: target,
      requestedExempt: exempt,
      transactionConfirmed: true,
      eventAccount: target,
      eventExempt,
      eventUpdatedBy: this.o.owner,
      finalOnChainExempt: finalExempt,
      verificationPassed,
      verificationFailures: failures,
      verified: verificationPassed,
    };
  }
}

function buildMockVerification(
  config: TokenConfig,
  supplySmallestUnits: bigint,
  failed: boolean
): VerificationResult {
  const checks: VerificationCheck[] = [
    { key: "blockTimestamp", label: "Block timestamp available", passed: true },
    { key: "eventPresent", label: "TokenCreated event present", passed: true },
    { key: "eventFactory", label: "Event emitted by configured factory", passed: true },
    { key: "eventCreator", label: "Event creator is the submitting wallet", passed: true },
    { key: "tokenNonZero", label: "Token address is non-zero", passed: true },
    { key: "eventName", label: "Event name matches", passed: true, expected: config.name, observed: config.name },
    { key: "eventSymbol", label: "Event symbol matches", passed: true },
    {
      key: "eventSupply",
      label: "Event supply matches",
      passed: !failed,
      expected: supplySmallestUnits.toString(),
      observed: failed ? (supplySmallestUnits - 1n).toString() : supplySmallestUnits.toString(),
    },
    { key: "eventFee", label: "Event feePaid matches effective fee", passed: true },
    { key: "tokenBytecode", label: "Token bytecode exists", passed: true },
    { key: "erc20Name", label: "ERC-20 name() matches", passed: true },
    { key: "erc20Symbol", label: "ERC-20 symbol() matches", passed: true },
    { key: "erc20Decimals", label: "ERC-20 decimals() == 18", passed: true, expected: "18", observed: "18" },
    { key: "erc20Supply", label: "ERC-20 totalSupply() matches", passed: !failed },
    { key: "creatorBalance", label: "Creator holds the full supply", passed: true },
  ];
  const failures = checks.filter((c) => !c.passed);
  return { passed: failures.length === 0, checks, failures };
}
